import React, { useEffect, useState, useCallback } from "react";
import { api, CONFIG } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BestAttendance } from "@/pages/Attendance";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Award, Trophy, Printer, ChevronLeft, ChevronRight, Vote, CheckCircle2, Eye, Lock, ShieldAlert } from "lucide-react";

const printCertificate = ({ name, monLong, subtitle, scoreLine }) => {
  const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const company = esc(CONFIG.company?.name || "ROZFA GROUP SDN BHD");
  const logo = window.location.origin + "/rozfa-logo.jpg";
  const html = `<!doctype html><html><head><title>Certificate</title><meta charset="utf-8"><style>
    *{font-family:Georgia,'Times New Roman',serif;box-sizing:border-box}
    body{margin:0;padding:0}
    .cert{width:100%;min-height:96vh;margin:12px;padding:40px;border:6px double #b4891f;text-align:center;display:flex;flex-direction:column;justify-content:center;background:#fffdf6}
    .logo{height:72px;width:auto;margin:0 auto 10px;display:block}
    .co{font-size:17px;letter-spacing:2px;text-transform:uppercase;color:#1e2a66;font-weight:bold}
    .reg{font-size:12px;color:#64748b;margin-top:2px}
    .title{font-size:40px;color:#b4891f;margin:22px 0 4px;font-weight:bold}
    .sub{font-size:18px;color:#475569;margin-bottom:28px}
    .awarded{font-size:14px;color:#64748b}
    .name{font-size:46px;margin:8px 0;color:#0f172a;border-bottom:2px solid #e2c477;display:inline-block;padding:0 24px 6px}
    .month{font-size:20px;color:#334155;margin-top:18px}
    .score{font-size:16px;color:#15803d;margin-top:8px}
    .foot{margin-top:40px;font-size:12px;color:#94a3b8}
    @media print{@page{size:landscape;margin:0}}
  </style></head><body>
    <div class="cert">
      <img class="logo" src="${logo}" alt="Rozfa" />
      <div class="co">${company}</div>
      <div class="reg">202101022034 (202101022034)</div>
      <div class="title">Certificate of Excellence</div>
      <div class="sub">${esc(subtitle)}</div>
      <div class="awarded">This certificate is proudly presented to</div>
      <div class="name">${esc(name)}</div>
      <div class="month">for outstanding performance in ${esc(monLong)}</div>
      <div class="score">${esc(scoreLine)}</div>
      <div class="foot">Awarded by a confidential vote of the Rozfa team &bull; ${new Date().toLocaleDateString("en-GB")}</div>
    </div>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return toast.error("Please allow pop-ups to print");
  w.document.write(html); w.document.close(); w.focus();
  setTimeout(() => w.print(), 600);
};

function BestPerformance() {
  const { user } = useAuth();
  const isSuper = !!user?.is_super;
  const isAdmin = user?.role === "admin" || isSuper;
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [voters, setVoters] = useState(null);
  const [showVoters, setShowVoters] = useState(false);
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const mk = `${y}-${String(m + 1).padStart(2, "0")}`;
  const monLong = cursor.toLocaleString("en-GB", { month: "long", year: "numeric" });

  const load = useCallback(() => api.get("/awards/performance", { params: { month: mk } }).then((r) => setData(r.data)).catch(() => {}), [mk]);
  const loadHistory = useCallback(() => api.get("/awards/performance/winners", { params: { months: 6 } }).then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const now = new Date();
  const atCurrent = y > now.getFullYear() || (y === now.getFullYear() && m >= now.getMonth());
  const canVote = !!data?.is_current && !data?.locked && !data?.future;
  const candidates = data?.candidates || [];
  const winner = candidates.find((c) => c.winner);

  const vote = async (cid) => {
    if (!canVote) return;
    try {
      const r = await api.post("/awards/performance/vote", { candidate_id: cid });
      setData(r.data);
      loadHistory();
      toast.success("Vote saved — you can change it anytime this month");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to vote"); }
  };
  const openVoters = async () => {
    try { const r = await api.get("/awards/performance/voters", { params: { month: mk } }); setVoters(r.data); setShowVoters(true); }
    catch { toast.error("Failed to load voters"); }
  };
  const pickWinner = async (cid) => {
    try { const r = await api.post("/awards/performance/winner", { month: mk, winner_id: cid }); setData((prev) => ({ ...r.data, my_vote: prev?.my_vote, can_see_voters: prev?.can_see_voters })); loadHistory(); toast.success("Winner selected"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const monthName = (mstr) => { const [yy, mm] = mstr.split("-").map(Number); return new Date(yy, mm - 1, 1).toLocaleString("en-GB", { month: "short", year: "numeric" }); };

  return (
    <div className="space-y-4" data-testid="best-performance">
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Best Performance of the Month</div>
          <p className="text-xs text-muted-foreground max-w-xl">Decided by a confidential staff vote. Every active staff member casts one vote a month and may change it any time until the month ends. Only vote totals are public — never who voted for whom.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && <Badge className={data.future ? "bg-sky-600" : data.is_current ? "bg-emerald-600" : "bg-slate-500"}>{data.future ? "Upcoming" : data.is_current ? "Voting open" : "Locked"}</Badge>}
          {data?.can_see_voters && candidates.length > 0 && (
            <Button size="sm" variant="outline" onClick={openVoters} data-testid="perf-view-voters"><Eye className="w-4 h-4 mr-1" /> Voter breakdown</Button>
          )}
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="perf-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold w-36 text-center" data-testid="perf-month">{monLong}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={atCurrent} onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="perf-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </Card>

      {data?.future && <Card className="p-10 text-center text-muted-foreground" data-testid="perf-future">This month hasn't started yet.</Card>}

      {!data?.future && winner && (
        <Card className="p-4 bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-950/40 dark:to-amber-900/20 border-amber-300 flex items-center gap-3 flex-wrap" data-testid="perf-announce">
          <Trophy className="w-8 h-8 text-amber-500 shrink-0" />
          <div className="text-sm flex-1 min-w-[12rem]">
            <span className="font-semibold">{winner.name}</span> is the Best Performance for <span className="font-semibold">{monLong}</span> with <span className="font-semibold">{winner.votes} vote{winner.votes === 1 ? "" : "s"}</span>! 🎉
          </div>
          {isAdmin && <Button size="sm" variant="outline" className="border-amber-400 text-amber-700" onClick={() => printCertificate({ name: winner.name, monLong, subtitle: "Best Performance of the Month", scoreLine: `Voted Best Performance by the team (${winner.votes} vote${winner.votes === 1 ? "" : "s"})` })} data-testid="perf-cert"><Printer className="w-4 h-4 mr-1" /> Certificate</Button>}
        </Card>
      )}

      {!data?.future && data?.tie && (
        <Card className="p-4 border-rose-300 bg-rose-50 dark:bg-rose-950/20 space-y-2" data-testid="perf-tie">
          <div className="text-sm font-semibold flex items-center gap-2 text-rose-700"><ShieldAlert className="w-4 h-4" /> Tie — awaiting decision</div>
          <p className="text-xs text-muted-foreground">{(data.tie_candidates || []).map((c) => `${c.name} (${c.votes})`).join(" · ")} are tied.
            {isSuper ? " As the master admin, select the winner:" : " The master admin will select the winner."}</p>
          {isSuper && (
            <div className="flex flex-wrap gap-2">
              {(data.tie_candidates || []).map((c) => (
                <Button key={c.id} size="sm" variant="outline" onClick={() => pickWinner(c.id)} data-testid={`perf-pick-${c.id}`}><Trophy className="w-4 h-4 mr-1 text-amber-500" /> {c.name}</Button>
              ))}
            </div>
          )}
        </Card>
      )}

      {!data?.future && (
        <Card className="p-4" data-testid="perf-vote-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="text-sm font-semibold flex items-center gap-2"><Vote className="w-4 h-4" /> {canVote ? "Tap a colleague to cast your vote" : "Voting closed — final results"}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              {canVote ? <Lock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              Your vote is confidential • {data?.total_votes || 0} total vote{(data?.total_votes || 0) === 1 ? "" : "s"}
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {candidates.map((c) => {
              const mine = data?.my_vote === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => vote(c.id)}
                  disabled={!canVote}
                  data-testid={`perf-candidate-${c.id}`}
                  className={`text-left rounded-lg border p-3 flex items-center justify-between gap-3 transition-colors ${c.winner ? "border-2 border-amber-400 bg-amber-50/60 dark:bg-amber-950/20" : mine ? "border-cyan-500 bg-cyan-50 dark:bg-cyan-950/20" : "hover:bg-muted/50"} ${canVote ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2 truncate">
                      {c.winner && <Trophy className="w-4 h-4 text-amber-500 shrink-0" />}
                      {c.name}
                      {mine && <Badge className="bg-cyan-600 shrink-0" data-testid={`perf-myvote-${c.id}`}>Your vote</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono uppercase truncate">{c.role}{c.is_leader && !c.winner ? " • leading" : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xl font-bold" data-testid={`perf-votes-${c.id}`}>{c.votes}</div>
                    <div className="text-[10px] text-muted-foreground">vote{c.votes === 1 ? "" : "s"}</div>
                  </div>
                </button>
              );
            })}
          </div>
          {canVote && data?.my_vote && <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> You've voted. Tap another colleague to change it before the month ends.</p>}
        </Card>
      )}

      {history.length > 0 && (
        <Card className="p-4" data-testid="perf-history">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Past Winners</div>
          <div className="divide-y">
            {history.map((h) => (
              <div key={h.month} className="flex items-center justify-between py-1.5 text-sm" data-testid={`perf-history-${h.month}`}>
                <span className="text-muted-foreground w-24">{monthName(h.month)}{h.is_current && <span className="text-[10px] text-emerald-600"> (live)</span>}</span>
                <span className="flex-1 font-medium">{h.winner || (h.tie ? <span className="text-rose-600 font-normal">Tie — awaiting decision</span> : <span className="text-muted-foreground font-normal">—</span>)}</span>
                {h.votes != null && <span className="text-xs text-muted-foreground">{h.votes} vote{h.votes === 1 ? "" : "s"}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Dialog open={showVoters} onOpenChange={setShowVoters}>
        <DialogContent className="max-w-lg" data-testid="perf-voters-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Voter breakdown — {monLong}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Visible only to the master admin. {(voters || []).length} vote(s) cast.</p>
          <div className="max-h-96 overflow-y-auto divide-y">
            {(voters || []).map((v) => (
              <div key={v.voter_id} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium">{v.voter}</span>
                <span className="text-muted-foreground">voted for <span className="font-medium text-foreground">{v.candidate}</span></span>
              </div>
            ))}
            {(voters || []).length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No votes cast yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Awards() {
  return (
    <div className="space-y-4" data-testid="awards-page">
      <div>
        <div className="text-xs font-mono text-muted-foreground uppercase">Recognition</div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2"><Award className="w-6 h-6" /> Award Centre</h1>
        <p className="text-sm text-muted-foreground">Celebrate the month's standout staff — Best Performance (team vote) and Best Attendance.</p>
      </div>
      <Tabs defaultValue="performance">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="performance" data-testid="award-tab-performance">Best Performance</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="award-tab-attendance">Best Attendance</TabsTrigger>
        </TabsList>
        <TabsContent value="performance"><BestPerformance /></TabsContent>
        <TabsContent value="attendance"><BestAttendance /></TabsContent>
      </Tabs>
    </div>
  );
}
